import { SingleReview } from "@/comps/feature/reviews"
import { WrappedFooter } from "@/comps/footer"
import Header from "@/comps/header"
import Runner from "@/comps/home/platforms/runner"
import { ErrorMessage } from "@/comps/message"
import Meta from "@/comps/meta"
import {
	PaymentSessionData,
	PAYMENT_DATA_KEY,
	useAdjustLogger,
} from "@/utils/adjust"
import { assetUrl } from "@/utils/cdn"
import useAuth from "@/utils/client-auth"

import {
	getAdId,
	Plans,
	plansSchema,
	pricesSchema,
} from "@/comps/subscription-popup"
import { NotificationContext } from "@/utils/notification"
import { base, code } from "@/utils/queryfy"
import { wrapSSRAuth } from "@/utils/ssr-auth"
import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import {
	PaymentStripeUrlRequest,
	PaymentStripeUrlResponse,
} from "pages/api/payment-stripe-url"
import { PricingPacksResponse } from "pages/api/pricing-packs"
import { useContext, useState } from "react"
import Compare from "sections/pricing/compare"
import PricingOrCoin from "sections/pricing/pricing-or-coin"
import Question from "sections/pricing/question"
import SubscriptionDetails from "sections/pricing/subscription-details"
import planJson from "statics/offering-info.json"
import priceJson from "statics/offering-prices.json"
import { v4 } from "uuid"

const prices = pricesSchema.parse(priceJson)
const plansParsed = plansSchema.parse(planJson)
const comparePlans = transformProps(plansParsed)

type ActiveTabType = "pricing" | "coin"

interface ServerSidePricingProps {
	currentTab: ActiveTabType
	plans: PricingPacksResponse
}

export default function Pricing(
	props: ServerSidePricingProps,
) {
	const { plans } = props
	const { packageCompare } = comparePlans.result

	const { t } = useTranslation()
	const [activeState, setActiveState] =
		useState<ActiveTabType>(props.currentTab)
	const { userInfo } = useAuth()
	const router = useRouter()
	const { notify } = useContext(NotificationContext)
	const logAdjust = useAdjustLogger()

	const [isYearly, setIsMonthly] = useState(true)

	async function handlePurchaseClick(packId: string) {
		if (userInfo && !userInfo.isAnonymous) {
			fetchPaymentLink(packId)
		} else {
			router.push("/login")
		}
	}

	async function fetchPaymentLink(packId: string) {
		try {
			const sessionId = v4()
			const payload: PaymentStripeUrlRequest = {
				location: router.asPath,
				period: isYearly ? "yearly" : "monthly",
				adId: await getAdId(),
				sessionId,
				offering: packId,
			}

			const { url, currency, value } = await axios
				.post<PaymentStripeUrlResponse>(
					"/api/payment-stripe-url",
					payload,
				)
				.then((res) => res.data)

			logAdjust?.logEvent("initiate_checkout")

			const paymentData: PaymentSessionData = {
				currency,
				value,
				session: sessionId,
			}

			localStorage.setItem(
				PAYMENT_DATA_KEY,
				JSON.stringify(paymentData),
			)

			router.push(url)
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_could_not_purchase_now")}
				</ErrorMessage>,
			)
		}
		window.blur()
	}

	return (
		<>
			<Meta
				title={t("txt_plan_and_pricing")}
				description={t("txt_home_description")}
			/>
			<div className="h-screen w-full">
				<div>
					<Header isStatic />
				</div>

				{!userInfo.isAnonymous && <SubscriptionDetails />}

				<div className="flex w-full flex-col items-center justify-center gap-5 pt-[50px] tablet:gap-8 tablet:px-5 desktop:gap-[61px] desktop:pt-[100px]">
					<h1 className="w-[250px] text-center text-[20px] font-700 text-blue-900 tablet:w-[300px] tablet:text-[24px] desktop:w-[400px] desktop:text-[32px]">
						{t("txt_pricing_title")}
					</h1>

					<div
						id="pricing"
						className="flex w-full flex-col gap-3 tablet:w-[512px]">
						<div className="flex w-full flex-row items-center justify-between tablet:justify-center tablet:gap-[10px]">
							<button
								onClick={(e) => {
									e.preventDefault()
									setActiveState("pricing")
								}}
								className={clsx(
									"flex w-1/2 items-center justify-center text-[14px] transition-all tablet:w-[251px] tablet:text-[18px]",
									activeState === "pricing"
										? "font-700 text-blue-900"
										: "font-600 text-blue-500",
								)}>
								{t("txt_business_plans")}
							</button>
							<button
								onClick={(e) => {
									e.preventDefault()
									setActiveState("coin")
								}}
								className={clsx(
									"flex w-1/2 items-center justify-center text-[14px] transition-all tablet:w-[251px] tablet:text-[18px]",
									activeState === "coin"
										? "font-700 text-blue-900"
										: "font-600 text-blue-500",
								)}>
								{t("txt_coins")}
							</button>
						</div>

						<div className="relative h-[1px] w-full bg-blue-300">
							<div
								className={clsx(
									"absolute top-[-0.5px] h-[2px] w-1/2 bg-blue-900 transition-all duration-300 tablet:w-[251px]",
									activeState === "pricing"
										? "left-0"
										: "left-1/2 tablet:left-[261px]",
								)}></div>
						</div>
					</div>
				</div>

				<PricingOrCoin
					activeState={activeState}
					isYearly={isYearly}
					setIsMonthly={setIsMonthly}
					plans={prices.plans}
					savePercent={prices.save_percent}
					handlePurchaseClick={handlePurchaseClick}
					basic={plans.plans.basic}
					advanced={plans.plans.advanced}
				/>

				<Runner
					className="mt-[32px] tablet:mt-[60px] desktop:mt-[100px]"
					bgColor="background"
				/>

				{activeState === "pricing" && (
					<Compare
						sections={packageCompare.sections}
						packages={packageCompare.packages}
						isYearly={isYearly}
						setIsMonthly={setIsMonthly}
						handlePurchaseClick={handlePurchaseClick}
						savePercent={prices.save_percent}
						basic={plans.plans.basic}
						advanced={plans.plans.advanced}
					/>
				)}

				<div
					className={clsx(
						activeState === "coin"
							? "desktop:pb-0"
							: "desktop:pb-[40px]",
						"flex w-full items-center justify-center pt-[48px]",
					)}>
					<img
						src={assetUrl("/general/editor-choice.svg")}
						alt="editor choice"
						className="h-[60px] w-[100px]"
					/>
				</div>

				<div
					className={clsx(
						activeState === "coin" && "hidden",
						"flex w-full flex-col items-center gap-[32px]",
					)}>
					<span className="text-[24px] font-700">
						{t("txt_we_trust")}
					</span>
					<div
						className={clsx(
							"no-scrollbar flex w-full gap-[20px] overflow-x-scroll",
							"px-[20px] tablet:justify-center desktop:items-center",
						)}>
						{prices.reviews.map((review, index) => {
							return (
								<SingleReview
									review={{
										name: review.name,
										rating: review.stars,
										opinion: review.text,
										avatar: review.image,
									}}
									avatar={review.image}
									key={index}
								/>
							)
						})}
					</div>
				</div>

				<div
					className={clsx(
						"flex w-full flex-col items-center gap-[30px] py-[60px]",
						"desktop:flex-row desktop:items-start desktop:justify-center desktop:gap-[24px]",
						"desktop:pt-[110px]",
					)}>
					<p className="text-h5 desktop:w-[272px]">
						{t("txt_asked_questions")}
					</p>
					<div
						className={clsx(
							"flex w-full flex-col gap-[16px] px-[20px] desktop:w-[775px] desktop:p-0",
							"no-scrollbar max-h-[724px] overflow-y-scroll tablet:items-center",
						)}>
						{prices.faq.map((question, index) => {
							return (
								<Question
									question={question.q}
									answer={question.a}
									key={index}
								/>
							)
						})}
					</div>
				</div>

				<div>
					<WrappedFooter />
				</div>
			</div>
		</>
	)
}

async function getPricesSSR(token: string) {
	const endpoint = `${base}/${code}/v2/offering/prices-new`

	const result = await axios
		.get<PricingPacksResponse>(endpoint, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data)

	return result
}

export const getServerSideProps =
	wrapSSRAuth<ServerSidePricingProps>(
		async (context, userInfo) => {
			const { type } = context.query

			let activeTab: ActiveTabType = "pricing"
			if (type === "coin") {
				activeTab = "coin"
			}

			const plans = await getPricesSSR(userInfo.accessToken)

			const props = {
				currentTab: activeTab,
				plans,
			}
			return props
		},
		[],
	)

function transformProps(plans: Plans): PlansType {
	const camelCasePlans: PlansType = {
		result: {
			packages: plans.packages.map((packageItem) => ({
				id: packageItem.id,
				title: packageItem.title,
				bestForAI: packageItem.best_for_ai,
			})),
			packageCompare: {
				packages: plans.package_compare.packages.map(
					(packageItem) => ({
						id: packageItem.id,
						title: packageItem.title,
					}),
				),
				sections: plans.package_compare.sections.map(
					(section) => ({
						title: section.title,
						features: section.features.map((feature) => ({
							title: feature.title,
							compareInfo: {
								free: feature.package_info.free,
								basic: feature.package_info.basic,
								advanced: feature.package_info.advanced,
								enterprise: feature.package_info.enterprise,
							},
							tooltip: feature.tooltip,
						})),
					}),
				),
			},
			availablePlans: plans.available_plans.map(
				(availablePlan) => ({
					id: availablePlan.id,
					title: availablePlan.title,
					weight: availablePlan.weight,
				}),
			),
		},
	}

	return camelCasePlans
}

type PlansType = {
	result: {
		packages: {
			id: string
			title: string
			bestForAI: boolean
		}[]
		packageCompare: {
			packages: {
				id: string
				title: string
			}[]
			sections: {
				title: string
				features: {
					title: string
					compareInfo: {
						free: string
						basic: string
						advanced: string
						enterprise: string
					}
				}[]
			}[]
		}
		availablePlans: {
			id: string
			title: string
			weight: number
		}[]
	}
}
